<template>
  <div>
    <MainLayout>
      <template v-if="currentPipelineReview" #title-adjacent>
        <StatusBadge :status="currentPipelineReview.status" />
      </template>
      <div v-if="loaded && teammate" class="flex justify-between items-start gap-4">
        <div class="lg:w-2/3">
          <Card>
            <CardContent>
              <header class="font-bold">Visible to Agent</header>
              <p class="text-sm text-gray-500 mb-8">Questions and answers cannot be changed.</p>

              <HFetchQuery :query="answersQuery">
                <template #default="{ data: questionsAndAnswers }">
                  <div v-for="qa in questionsAndAnswers" :key="qa.question_id">
                    <label class="question-label">{{ qa.question }}</label>
                    <div class="answer-display user-generated-content" v-html="sanitizeRichText(qa.answer ?? '')"></div>
                  </div>
                </template>
              </HFetchQuery>

              <hr class="my-8 -ml-6 -mr-6" />

              <header class="font-bold">Private</header>
              <p class="text-sm text-gray-500 mb-8">This is not visible to the agent.</p>

              <QuillInput v-model="privateNotes" label="Your Private Notes" name="private_notes" />

              <label class="mt-4 block text-sm leading-5 font-medium text-gray-700 mb-1">Pipeline Performance</label>
              <ScorePicker :score="score" @set-score="s => (score = s)" />

              <Button class="mt-8" @click="submitReview">Save pipeline review</Button>
            </CardContent>
          </Card>
        </div>

        <div class="lg:w-1/3">
          <EventRecordingCard :event-recording="eventRecording" />
          <TeammateInfoCard :teammate="teammate" />
          <AttachmentsCard v-if="currentPipelineReview" class="mt-6" :pipeline-review="currentPipelineReview" />
        </div>
      </div>
    </MainLayout>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import MainLayout from '@/components/layouts/MainLayout/index.vue';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import QuillInput from '@/components/shared/QuillInput.vue';
import ScorePicker from '@/components/shared/pipeline_reviews/ScorePicker.vue';
import AttachmentsCard from './AttachmentsCard.vue';
import EventRecordingCard from './EventRecordingCard.vue';
import TeammateInfoCard from './TeammateInfoCard.vue';
import HFetchQuery from '@/lib/hfetch/HFetchQuery.vue';
import { useHFetchQuery } from '@/lib/hfetch/useHFetchQuery';
import { sanitizeRichText } from '@/utilities/sanitization';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner';
import { toast } from 'vue-sonner';
import { Api } from '@/api';
import type { PipelineReviewShowRecord } from '@/api/pipeline_reviews';
import type { EventRecordingEntity } from '@/api/entity_types/EventRecording';
import StatusBadge from '@/components/shared/pipeline_reviews/StatusBadge.vue';

const route = useRoute();
const router = useRouter();
const loadingSpinnerStore = useLoadingSpinnerStore();

const contactId = Number(route.params.contactId);
const reviewId = Number(route.params.id);

const loaded = ref(false);
const teammate = ref<{ id: number; name: string; email: string; phone_number: string } | null>(null);
const currentPipelineReview = ref<PipelineReviewShowRecord | null>(null);
const eventRecording = ref<EventRecordingEntity | null>(null);
const privateNotes = ref('');
const score = ref<number>(0);

const answersQuery = useHFetchQuery(() => Api.PipelineReviewAnswers.listAnswers(reviewId));

const submitReview = async () => {
  loadingSpinnerStore.showLocalLoadingSpinner();

  const response = await Api.PipelineReviews.updatePipelineReview(contactId, reviewId, {
    private_notes: privateNotes.value,
    score: score.value,
  });

  loadingSpinnerStore.hideLocalLoadingSpinner();

  if (response.status === 'success') {
    toast.success('Pipeline Review Updated', {
      description: 'Your pipeline review was successfully updated.',
    });
    router.push(`/team/${contactId}/pipeline_reviews/${reviewId}`);
  } else {
    toast.error('Pipeline Review Error', {
      description: response.messages.join(', '),
    });
  }
};

onMounted(async () => {
  const [reviewData, contactData] = await Promise.all([
    Api.PipelineReviews.getPipelineReview(contactId, reviewId),
    Api.Contacts.getContact(String(contactId)),
  ]);

  if (reviewData.status === 'success') {
    if (reviewData.data.pipeline_review.stage !== 'ready_for_review') {
      toast.error('Pipeline Review Not Ready', {
        description: 'This pipeline review is not ready to be reviewed yet.',
      });
      router.replace(`/team/${contactId}/pipeline_reviews/${reviewId}`);
      return;
    }
    currentPipelineReview.value = reviewData.data.pipeline_review;
    eventRecording.value = reviewData.data.contact_event?.event_recording ?? null;
    privateNotes.value = reviewData.data.pipeline_review.result?.private_notes ?? '';
    score.value = reviewData.data.pipeline_review.result?.score ?? 0;
  }
  teammate.value = contactData?.record;
  loaded.value = true;
});
</script>

<style scoped>
@reference "tailwindcss";

.question-label {
  @apply mt-4 block text-sm leading-5 font-medium text-gray-700;
}

.answer-display {
  @apply shadow-sm bg-gray-200 border p-8 text-sm mt-1 border-gray-300 rounded-md;
}
</style>
